import React, {useEffect, useState} from "react";
import {BUNDLE_ICON} from "../Constants";
import WorkspaceTable from "../components/Workspace/WorkspaceTable";
import WorkspaceControls from "../components/Workspace/WorkspaceControls";
import CompanyContactPickerDialog from "../components/Contacts/CompanyContactPickerDialog";
import OpportunityAttachControl from "../components/Workspace/OpportunityAttachControl";
import {PREPARED_CART_ICON} from "../Constants";
import {ICompany, IContact} from "../models/Company";
import ExtensionModal from "../components/ExtensionModal/ExtensionModal";
import {Spinner, SpinnerSize, Stack, Text, useTheme} from "@fluentui/react";
import {IWorkspaceItem} from "../models/Workspace";
import WorkspaceTotalsDisplay from "../components/Workspace/WorkspaceTotalsDisplay";
import {IInstalledExtension, IRealTimeExtensionResponse} from "../models/Extension";
import {
    useGetContactByIdQuery,
    useGetInstalledExtensionsQuery, useGetRecentWorkspaceRefsQuery,
    useGetWorkspaceProductsQuery,
    useGetWorkspaceQuery, usePutWorkspaceMutation,
    useRunRealtimeExtensionMutation, useSetSelectedWorkspaceMutation} from '../store/Api';
import ContactDetailsModal from "../components/Contacts/ContactDetailsModal";
import CompanyDetailsModal from "../components/Contacts/CompanyDetailsModal";
import RecommendedItemsDisplay from "../components/Workspace/RecommendedItemsDisplay";
import DetailsModal from "../components/DetailsModal/DetailsModal";
import useCan, {EDIT_ITEM, STOREFRONT_ADMIN} from "../permissions/ability";
import WorkspaceImportModal from "../components/WorkspaceImportModal/WorkspaceImportModal";
import {RouteComponentProps} from "react-router-dom";
import {toast} from "react-toastify";
import {DefaultButton, Dialog, DialogFooter, DialogType, Icon, PrimaryButton} from "@fluentui/react";
import {humanizeRecordType,
    IRecordLink,
    useGetWorkspaceLinksQuery,
    useLazyResolveRecordUrlQuery, useDeleteWorkspaceLinkMutation} from '../store/endpoints/RecordLinksEndpoints';
import CustomerRequestWindow from "../components/CustomerRequest/CustomerRequestWindow";
import AdHocItemModel from "../components/Workspace/AdHocItemModel";
import { IsMutationSuccess } from "../logic/MutationTypeChecker";
import Footer from "../components/Layout/Footer";
import EditableText from "../components/Inputs/EditableText";
import {useGetOrganizationEntitlementsQuery} from "../store/endpoints/OrganizationsEndpoints";
import {useSelector} from "react-redux";
import {RootState} from "../store/rootReducer";
import ProductBundleWorkspaceTable from "../components/ProductBundles/ProductBundleWorkspaceTable";
import {useGetProductBundleRefsQuery, useAddProductBundleToWorkspaceMutation} from "../store/endpoints/ProductBundleEndpoints";
import {IProductBundleRef} from "../models/ProductBundles";
import {PREPARED_CART_PANEL_WIDTH, PreparedCartPanel} from "../components/Storefront/PreparedCart/PreparedCartPanel";
import {useGetPreparedCartQuery, usePatchPreparedCartMutation} from "../store/endpoints/PreparedCartEndpoints";
import StorefrontBundleModal from "../components/ProductBundles/StorefrontBundleModal";
import WorkspaceBundleConfigPanel from "../components/Workspace/WorkspaceBundleConfigPanel";
import ConfiguredBundleDetailsPanel from "../components/Workspace/ConfiguredBundleDetailsPanel";

interface IWorkspacePageProps{
    workspaceId?: string;
}
const WorkspacePage: React.FC<RouteComponentProps<IWorkspacePageProps>> = (props) => {
    const [selectedWorkspace, setSelectedWorkspace] = useState<string>();
    const [selectedItems, setSelectedItems] = useState<IWorkspaceItem[]>([]);
    const [workspaceItems, setWorkspaceItems] = useState<IWorkspaceItem[]>([])
    const [selectedExtension, setSelectedExtension] = useState<IInstalledExtension | undefined>();
    const installedExtensions = useGetInstalledExtensionsQuery();
    const recentWorkspaces = useGetRecentWorkspaceRefsQuery()
    const [detailsItem, setDetailsItem] = useState<IWorkspaceItem | undefined>();
    const canEditItem = useCan(EDIT_ITEM);
    const {data: workspaceData, isLoading: searchIsLoading, refetch: refetchWorkspace} = useGetWorkspaceQuery(selectedWorkspace ?? "",
        {skip: (!selectedWorkspace || selectedWorkspace.trim().length === 0)})
    const products = useGetWorkspaceProductsQuery(selectedWorkspace ?? "", 
        {skip: (!selectedWorkspace || selectedWorkspace.length === 0)})
    const [updateWorkspace] = usePutWorkspaceMutation();
    const [workspaceName, setWorkspaceName] = useState<string>()
    const [importModalOpen, setImportModalOpen] = useState<boolean>(false);
    const [customerRequestModalOpen, setCustomerRequestModalOpen] = useState<boolean>(false);
    const [adHocItem, setAdHocItem] = useState<IWorkspaceItem | undefined>();
    const [adHocOpen, setAdHocOpen] = useState<boolean>(false);
    const [prepareCartOpen, setPrepareCartOpen] = useState<boolean>(
        new URLSearchParams(props.location.search).has('showPreparedCart')
    );
    // When an import hands off ("Prepare cart after import" or the PSA quote-link quick flow),
    // open the cart panel on its final step.
    const [prepareCartFocusDeliver, setPrepareCartFocusDeliver] = useState<boolean>(
        new URLSearchParams(props.location.search).has('focusDeliver')
    );
    // Workspace lines the prepared cart's estimate could not source; highlighted while the panel is open.
    const [problemItemIds, setProblemItemIds] = useState<string[]>([]);
    const closePrepareCart = () => {
        setPrepareCartOpen(false);
        setPrepareCartFocusDeliver(false);
        setProblemItemIds([]);
    };
    const canStorefront = useCan(STOREFRONT_ADMIN);
    const workspaceLinks = useGetWorkspaceLinksQuery(selectedWorkspace ?? '',
        {skip: !selectedWorkspace || selectedWorkspace.trim().length === 0});
    const [resolveRecordUrl] = useLazyResolveRecordUrlQuery();

    const openCrmRecord = async (link: IRecordLink) => {
        try {
            const res = await resolveRecordUrl({folioId: link.folioId, recordType: link.recordType, recordId: link.recordId}).unwrap();
            if (res.supported && res.url) {
                window.open(res.url, '_blank', 'noopener');
            } else {
                toast.error(`This ${humanizeRecordType(link.recordType)} can't be opened in ${link.folioName ?? 'the CRM'}`);
            }
        } catch {
            toast.error('Failed to open record');
        }
    };
    const preparedCart = useGetPreparedCartQuery(
        { workspaceId: selectedWorkspace ?? '' },
        { skip: !selectedWorkspace || selectedWorkspace.trim().length === 0 }
    );
    const attachedContact = useGetContactByIdQuery(
        {contactId: workspaceData?.contactId ?? '', includeCompanyDetails: true},
        {skip: !workspaceData?.contactId});
    const [showAttachedContact, setShowAttachedContact] = useState<boolean>(false);
    const [showAttachedCompany, setShowAttachedCompany] = useState<boolean>(false);
    // The company/contact picker, spawned from the header pill (it used to live behind an
    // "Attach Contact" command in the toolbar).
    const [contactPickerOpen, setContactPickerOpen] = useState<boolean>(false);
    // Detaching a CRM record (x on its pill) asks first; the imported items stay.
    const [detachLink, setDetachLink] = useState<IRecordLink>();
    const [deleteWorkspaceLink, deleteWorkspaceLinkState] = useDeleteWorkspaceLinkMutation();
    const confirmDetachLink = async () => {
        if (!detachLink || !workspaceData?.id) return;
        try {
            await deleteWorkspaceLink({workspaceId: workspaceData.id, folioId: detachLink.folioId, recordId: detachLink.recordId}).unwrap();
        } finally {
            setDetachLink(undefined);
            workspaceLinks.refetch();
        }
    };
    const hasOpportunityLink = (workspaceLinks.data ?? []).some(l => l.recordType.toLowerCase().includes('opportunit'));
    const afterOpportunityImport = () => {
        products.refetch();
        workspaceLinks.refetch();
        refetchWorkspace();
        if (!preparedCart.isUninitialized) preparedCart.refetch();
    };
    const [patchPreparedCart] = usePatchPreparedCartMutation();
    // The workspace and its prepared cart share one customer. Attaching or detaching from the header
    // pill writes both, so the pill, the cart panel, and exports all agree.
    const writeCustomer = async (companyId: string, companyName: string, contactId: string) => {
        if (!workspaceData?.id) return;
        await updateWorkspace({companyName, contactId, companyId, id: workspaceData.id, documentIsCurrent: false}).unwrap();
        if (preparedCart.data?.id) {
            await patchPreparedCart({
                id: workspaceData.id,
                request: {targetCompany: companyId, targetUsers: contactId ? [contactId] : []}
            }).unwrap().catch(() => undefined);
        }
        // The contact query follows the workspace's contactId, so refreshing the workspace is enough;
        // refetching it directly throws when it is skipped (e.g. right after a detach).
        refetchWorkspace();
    };
    const attachCustomer = async (company: ICompany, contact?: IContact) => {
        await writeCustomer(company.id ?? '', company.companyName ?? '', contact?.id ?? '');
        setContactPickerOpen(false);
    };
    const [confirmDetachCustomer, setConfirmDetachCustomer] = useState(false);
    const detachCustomer = () => setConfirmDetachCustomer(true);
    const confirmedDetachCustomer = async () => {
        setConfirmDetachCustomer(false);
        setShowAttachedContact(false);
        setShowAttachedCompany(false);
        await writeCustomer('', '', '').catch(() => undefined);
    };
    const [refreshingVendors, setRefreshingVendors] = useState<boolean>(false);
    const [realtimeErrors, setRealtimeErrors] = useState<{[key:string]: string}>({});
    const [showErrorCallout, setShowErrorCallout] = useState(false);
    const [runRealtime] = useRunRealtimeExtensionMutation();
    const organization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const getEntitlements = useGetOrganizationEntitlementsQuery(organization ?? '', {skip: !organization || organization === ''});
    const bundleRefs = useGetProductBundleRefsQuery({});
    const [addBundleToWorkspace] = useAddProductBundleToWorkspaceMutation();
    const [bundleCount, setBundleCount] = useState<number>(0);
    const [showBundles, setShowBundles] = useState(false);
    const theme = useTheme();
    const [showBundleDetails, setShowBundleDetails] = useState(false);
    const [selectedDetailsBundle, setSelectedDetailsBundle] = useState<string | undefined>();
    const [configBundleItem, setConfigBundleItem] = useState<IWorkspaceItem | undefined>();
    const [orderSelectionItem, setOrderSelectionItem] = useState<IWorkspaceItem | undefined>();
    const [selectedBundle, setSelectedBundle] = useState<IProductBundleRef | undefined>();


    useEffect(() => {
        if(props.match.params.workspaceId) {
            if(props.match.params.workspaceId === 'last' && recentWorkspaces.data && recentWorkspaces.data.length > 0){
                setSelectedWorkspace(recentWorkspaces.data[0].id)
            }
            else if(props.match.params.workspaceId && !(props.match.params.workspaceId === 'last')){
                setSelectedWorkspace(props.match.params.workspaceId)
            }
        }

    }, [props.match.params.workspaceId, recentWorkspaces]);

    useEffect(() => {
        setWorkspaceName(workspaceData?.name)
    }, [searchIsLoading, workspaceData]);

    // Publish the open workspace as the app-wide selected one. Column-header controls (taxable, tax
    // code, source) and add-to-workspace defaults read it from redux, and until now only the home
    // cards and the header nav wrote it, so a direct load of a workspace URL left them hidden.
    const [publishSelectedWorkspace] = useSetSelectedWorkspaceMutation();
    useEffect(() => {
        if (selectedWorkspace) void publishSelectedWorkspace(selectedWorkspace);
    }, [selectedWorkspace, publishSelectedWorkspace]);
    useEffect(() => {
        if(products.data) {
            setWorkspaceItems(products.data)
        }
    }, [products.data]);
    
    const applyNewWorkspaceName = (name?: string) => {
        if(name == undefined || name === workspaceName || name === '') return;
        setWorkspaceName(name);
        updateWorkspace({name: name, id: selectedWorkspace})
    }
    
    const workspaceBundleCommands = () => {
        return ([
            {
                key: 'bundles',
                // Same glyph the omnibox uses for bundles; the spinner only stands in while the count loads.
                onRenderIcon: () => (bundleRefs.isLoading || bundleRefs.isFetching)
                    ? <Spinner size={SpinnerSize.xSmall} />
                    : <Icon iconName={BUNDLE_ICON} styles={{root: {fontSize: 16, color: showBundles ? '#ffffff' : theme.palette.themePrimary}}} />,
                text: `Bundles ${!(bundleRefs.isLoading || bundleRefs.isFetching) && bundleCount ? `(${bundleCount})` : ''}`,
                buttonStyles: { root: { backgroundColor: showBundles ? 'rgb(120, 156, 47)': "unset", color: showBundles ? '#ffffff' : 'unset'},label: { fontWeight: bundleCount > 0 ? 'bold' : undefined } },
                onClick: () => {
                    setShowBundles(!showBundles);
                }
            }]);
    }
    
    const refreshVendors = async () => {
        if (!selectedWorkspace) return;
        setRefreshingVendors(true);
        const promises = [];
        for (const ext of installedExtensions.data ?? []) {
            if (ext.extensionType !== 'realtime' || !ext.enabled) continue;
            promises.push(
                runRealtime({extension: ext, workspace: selectedWorkspace ?? ""})
                .then((result) => {
                    if (IsMutationSuccess<IRealTimeExtensionResponse>(result)) {
                        if (result.data?.success) {
                            setRealtimeErrors((e) => {
                                const newErrors = {...e};
                                delete newErrors[ext.nickname ?? ext.name];
                                return newErrors;
                            });
                            return;
                        }
                        setRealtimeErrors({...realtimeErrors, [ext.nickname ?? ext.name]: result.data?.error ?? 'Unknown Error'});
                        setShowErrorCallout(true);
                        return;
                    }
                    setRealtimeErrors({...realtimeErrors, [ext.nickname ?? ext.name]: 'API Call Failed'});
                    setShowErrorCallout(true);
                })
            );
        }
        await Promise.all(promises);
        setRefreshingVendors(false);
    }

    const getSelectedItems = () => {
        if(selectedItems && selectedItems.length > 0){
            return selectedItems
        }
        if(products.data){
            return workspaceItems
        }
        return []
    }

    function onAdhocClick(item: IWorkspaceItem | undefined) {
        if (item) setAdHocItem(item);
        else setAdHocItem(undefined);
        setAdHocOpen(true);
    }

    return (
        // The prepared cart panel docks on the right without an overlay, so slide the page content
        // over by its width (the search page does the same for its criteria panel).
        <div className="main-content" style={{marginRight: prepareCartOpen ? PREPARED_CART_PANEL_WIDTH : 0, transition: 'margin-right 0.25s ease'}}>
            <Stack horizontal>
                <Stack.Item grow align='center'>
                    {(searchIsLoading || workspaceName == undefined || workspaceName === '') ? <Text variant='mediumPlus'>Loading Workspace...</Text> :
                    <div style={{paddingBottom: '5px', maxWidth: '600px', minWidth: '100px'}}>
                        <EditableText
                            value={workspaceName}
                            onChange={applyNewWorkspaceName}
                            valueRequired={true}
                            suffix={workspaceData?.isTemplate ? " Workspace (Template)" : " Workspace"}
                            placeholder={'Enter Workspace Name'}
                        />
                        {workspaceData && (
                            <div style={{ marginTop: '4px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                                {preparedCart.data?.storefrontId && <>
                                    <span
                                        title={prepareCartOpen ? 'Close the prepared cart panel' : 'This workspace has been set up as a prepared cart. Open the panel.'}
                                        role='button'
                                        aria-pressed={prepareCartOpen}
                                        onClick={() => prepareCartOpen ? closePrepareCart() : setPrepareCartOpen(true)}
                                        style={{
                                            cursor: 'pointer',
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            background: '#fff4ce',
                                            color: '#8a6914',
                                            border: '1px solid #f7c948',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                        }}
                                    >
                                        <Icon iconName={PREPARED_CART_ICON} style={{fontSize: 10}} />
                                        Prepared Cart
                                    </span>
                                    {preparedCart.data.delivered && (
                                        <span style={{
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            background: '#dff6dd',
                                            color: '#107c10',
                                        }}>
                                            Delivered
                                        </span>
                                    )}
                                    {preparedCart.data.ordered && (
                                        <span style={{
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            background: '#deecf9',
                                            color: '#0078d4',
                                        }}>
                                            Ordered
                                        </span>
                                    )}
                                </>}
                                {workspaceLinks.data?.map((link) => (
                                    <span
                                        key={`${link.folioId}-${link.recordType}-${link.recordId}`}
                                        style={{
                                            padding: '2px 4px 2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            background: '#ede7f6',
                                            color: '#5e35b1',
                                            border: '1px solid #b39ddb',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                        }}
                                    >
                                        <span
                                            role={link.supported ? 'button' : undefined}
                                            title={link.supported
                                                ? `Open this ${humanizeRecordType(link.recordType)} in ${link.folioName ?? 'the CRM'}`
                                                : `${link.folioName ?? 'This CRM'} does not support direct links to ${humanizeRecordType(link.recordType)}s`}
                                            onClick={link.supported ? () => { void openCrmRecord(link); } : undefined}
                                            style={{cursor: link.supported ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: '4px'}}
                                        >
                                            <Icon iconName='OpenInNewWindow' style={{fontSize: 10}} />
                                            {link.recordType}
                                        </span>
                                        <span
                                            role='button'
                                            title={`Detach this ${humanizeRecordType(link.recordType)}`}
                                            aria-label={`Detach this ${humanizeRecordType(link.recordType)}`}
                                            onClick={(e) => { e.stopPropagation(); setDetachLink(link); }}
                                            style={{cursor: 'pointer', display: 'inline-flex', alignItems: 'center', padding: '0 2px', opacity: 0.8}}
                                        >
                                            <Icon iconName='Cancel' style={{fontSize: 10}} />
                                        </span>
                                    </span>
                                ))}
                                {workspaceData?.id &&
                                    <OpportunityAttachControl
                                        workspaceId={workspaceData.id}
                                        hasOpportunity={hasOpportunityLink}
                                        onImported={afterOpportunityImport}
                                    />}
                                {workspaceData?.companyName && (
                                    <span
                                        style={{
                                            padding: '2px 4px 2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            background: '#e2e9cb',
                                            color: '#43571a',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                        }}
                                    >
                                        <span
                                            role='button'
                                            title={attachedContact.data
                                                ? (showAttachedContact ? 'Close the attached contact' : 'Open the attached contact')
                                                : (showAttachedCompany ? 'Close the attached company' : 'Open the attached company')}
                                            aria-pressed={attachedContact.data ? showAttachedContact : showAttachedCompany}
                                            onClick={attachedContact.data
                                                ? () => setShowAttachedContact(open => !open)
                                                : () => setShowAttachedCompany(open => !open)}
                                            style={{cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px'}}
                                        >
                                            <Icon iconName='Contact' style={{fontSize: 10}} />
                                            {[workspaceData.companyName,
                                                [attachedContact.data?.firstName, attachedContact.data?.lastName].filter(Boolean).join(' ')]
                                                .filter(Boolean).join(' · ')}
                                        </span>
                                        <span
                                            role='button'
                                            title='Detach this company and contact'
                                            aria-label='Detach this company and contact'
                                            onClick={(e) => { e.stopPropagation(); detachCustomer(); }}
                                            style={{cursor: 'pointer', display: 'inline-flex', alignItems: 'center', padding: '0 2px', opacity: 0.8}}
                                        >
                                            <Icon iconName='Cancel' style={{fontSize: 10}} />
                                        </span>
                                    </span>
                                )}
                                {!workspaceData?.companyName && (
                                    <span
                                        role='button'
                                        title='Attach a company and contact to this workspace'
                                        onClick={() => setContactPickerOpen(true)}
                                        style={{
                                            cursor: 'pointer',
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            background: 'transparent',
                                            color: '#43571a',
                                            border: '1px dashed #a8c371',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                        }}
                                    >
                                        <Icon iconName='AddFriend' style={{fontSize: 10}} />
                                        Attach contact
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    }
                </Stack.Item>
                <Stack.Item align='end'>
                    {workspaceData?.id && <WorkspaceControls
                        selectedWorkspace={workspaceData.id}
                        removableItems={selectedItems}
                        disableExtensions={(products.data?.length ?? 0) === 0}
                        onRefresh={() => products.refetch()}
                        selectedItems={getSelectedItems()}
                        onExtensionSelection={(ext) => setSelectedExtension(ext)}
                        onImportSelection={() => setImportModalOpen(true)}
                        onCustomerRequestSelection={() => setCustomerRequestModalOpen(true)}
                        onAdhocClick={() => onAdhocClick(undefined)}
                        onPrepareCart={() => setPrepareCartOpen(true)}
                        refreshingVendors={refreshingVendors}
                        onRefreshVendors={refreshVendors}
                        externalCommandBarProps={workspaceBundleCommands()}
                    />}
                </Stack.Item>
            </Stack>
            {workspaceData?.id && <WorkspaceTotalsDisplay 
                workspace={workspaceData.id}
            />}
            {(installedExtensions.data && workspaceData?.id) && installedExtensions.data.filter(f => 
                f.extensionType === 'recommendation').map((ext) => 
                <RecommendedItemsDisplay 
                    extension={ext} 
                    workspace={workspaceData.id} 
                    key={`${ext.id}-recommendedItems`}
                />)}            
            {workspaceData?.id &&
                <React.Fragment>
                    <ProductBundleWorkspaceTable
                        showResults={showBundles}
                        onHide={() => setShowBundles(false)}
                        onResultsCountChange={(count) => setBundleCount(count)}
                        onSelectedBundleChange={(bundle) => setSelectedBundle(bundle)}
                        onOpenBundle={(bundleId) => {
                            setSelectedDetailsBundle(bundleId);
                            setShowBundleDetails(true);
                        }}
                        workspaceId={workspaceData.id}
                    />
                    <WorkspaceTable
                        highlightedItemIds={prepareCartOpen ? problemItemIds : undefined}
                        selectedItems={selectedItems}
                        onSelectedItemsChanged={(items) => setSelectedItems(items)}
                        workspace={workspaceData.id}
                        onDetailsClick={(i) => setDetailsItem(i)}
                        onAdhocClick={(i) => onAdhocClick((i))}
                        onBundleClick={(i) => {
                            setSelectedDetailsBundle(i?.sourceEntityId)
                            setShowBundleDetails(true);
                        }}
                        onBundleConfigClick={(i) => setConfigBundleItem(i)}
                        onOrderSelectionClick={(i) => setOrderSelectionItem(i)}
                        onExternalBundleDrop={async (ids) => {
                            if (!workspaceData?.id) return;
                            await Promise.all(ids.map(bundleId => addBundleToWorkspace({ bundleId, workspaceId: workspaceData.id }).unwrap()))
                        }}
                    />
                </React.Fragment>
            }
            {workspaceData?.id && selectedExtension &&
            <ExtensionModal
                workspaceId={workspaceData.id}
                extension={selectedExtension}
                isOpen={true}
                onClose={() => {
                    setSelectedExtension(undefined)
                    products.refetch()
                    // An import may have linked an opportunity or attached a contact.
                    workspaceLinks.refetch()
                    refetchWorkspace()
                }}
                onPrepareCart={canStorefront ? () => {
                    setPrepareCartFocusDeliver(true);
                    setPrepareCartOpen(true);
                } : undefined}
            />}
            {(detailsItem?.product) && 
                <DetailsModal 
                    product={detailsItem.productId}
                    catalog={detailsItem.catalog}
                    open={true} 
                    onClose={() => setDetailsItem(undefined)} 
                    preventEdit={!canEditItem} />}
            {adHocOpen &&
                <AdHocItemModel open={adHocOpen} onClose={() => setAdHocOpen(false)} workspace={workspaceData?.id ?? ""} product={adHocItem}/>}
            <WorkspaceImportModal
                isOpen={importModalOpen}
                onClose={() => {
                    setImportModalOpen(false)
                }}
                workspaceId={workspaceData?.id ?? ""}
                />
            {getEntitlements.data?.llmEnabled && <CustomerRequestWindow
                isOpen={customerRequestModalOpen}
                onClose={() => setCustomerRequestModalOpen(false)}
                workspaceId={selectedWorkspace}
            />}
            <PreparedCartPanel
                visible={prepareCartOpen}
                onClose={closePrepareCart}
                workspaceId={selectedWorkspace ?? ''}
                focusDeliver={prepareCartFocusDeliver}
                onProblemItemsChange={setProblemItemIds}
            />
            <StorefrontBundleModal
                isOpen={showBundleDetails}
                onDismiss={() => {
                    setShowBundleDetails(false);
                    setSelectedDetailsBundle(undefined);
                }}
                bundleId={selectedDetailsBundle ?? ''}/>
            <Dialog
                hidden={!confirmDetachCustomer}
                onDismiss={() => setConfirmDetachCustomer(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Detach this company and contact?',
                    subText: `${workspaceData?.companyName ?? 'The company'} is removed from this workspace and its prepared cart. The prepared cart cannot be delivered until another is attached.`
                }}
                modalProps={{isBlocking: true}}>
                <DialogFooter>
                    <PrimaryButton text='Detach' iconProps={{iconName: 'RemoveLink'}} onClick={() => void confirmedDetachCustomer()} />
                    <DefaultButton text='Cancel' onClick={() => setConfirmDetachCustomer(false)} />
                </DialogFooter>
            </Dialog>
            <Dialog
                hidden={!detachLink}
                onDismiss={() => setDetachLink(undefined)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: detachLink ? `Detach this ${humanizeRecordType(detachLink.recordType)}?` : 'Detach record?',
                    subText: detachLink
                        ? `The link between this workspace and ${detachLink.folioName ?? 'the CRM'} ${humanizeRecordType(detachLink.recordType)} ${detachLink.recordId} is removed. Imported items, company, and contact stay.`
                        : undefined
                }}
                modalProps={{isBlocking: true}}>
                <DialogFooter>
                    <PrimaryButton text='Detach' iconProps={{iconName: 'RemoveLink'}} disabled={deleteWorkspaceLinkState.isLoading} onClick={() => void confirmDetachLink()} />
                    <DefaultButton text='Cancel' onClick={() => setDetachLink(undefined)} />
                </DialogFooter>
            </Dialog>
            {workspaceData?.id &&
                <CompanyContactPickerDialog
                    isOpen={contactPickerOpen}
                    storefrontId={preparedCart.data?.storefrontId}
                    onDismiss={() => setContactPickerOpen(false)}
                    onSave={attachCustomer}
                />}
            {showAttachedContact && attachedContact.data &&
                <ContactDetailsModal
                    isOpen={true}
                    isBlocking={false}
                    contact={attachedContact.data}
                    onDismiss={(saved) => {
                        setShowAttachedContact(false);
                        if (saved) {
                            if (!attachedContact.isUninitialized) attachedContact.refetch();
                            refetchWorkspace();
                        }
                    }}
                />}
            {showAttachedCompany && workspaceData?.companyId &&
                <CompanyDetailsModal
                    isOpen={true}
                    isBlocking={false}
                    company={workspaceData.companyId}
                    onClose={() => {
                        setShowAttachedCompany(false);
                        refetchWorkspace();
                    }}
                />}
            <WorkspaceBundleConfigPanel
                isOpen={!!configBundleItem}
                onDismiss={() => setConfigBundleItem(undefined)}
                item={configBundleItem}/>
            <ConfiguredBundleDetailsPanel
                isOpen={!!orderSelectionItem}
                onDismiss={() => setOrderSelectionItem(undefined)}
                item={orderSelectionItem}/>
            <Footer className={'powered-by-footer-absolute'}/>
        </div>
    );
}

export default WorkspacePage;
